import { useCallback, useEffect, useRef, useState } from 'react';
import { getRate, subscribeRate } from './playbackRate';

/**
 * Точное проигрывание куска записи — через Web Audio по уже декодированным отсчётам.
 *
 * Раньше отрезок играл <audio>: перемотка на начало, play(), и пауза, когда
 * currentTime дошёл до конца. Оба края выходили неточными и РАЗНЫМИ от раза к
 * разу: перемотка сжатого файла встаёт не ровно туда, куда просили, а пауза
 * срабатывает на кадре отрисовки, когда звук уже ушёл дальше. Владелец 15.09:
 * «останавливается не в том месте дорожки, где обозначен её конец… от
 * проигрывания к проигрыванию разный».
 *
 * AudioBufferSourceNode.start(when, offset, duration) играет ровно этот кусок
 * отсчётов: начало и конец задаются в отсчётах, а не ловятся по часам элемента.
 * Позиция для бегунка — по часам AudioContext с поправкой на задержку вывода,
 * чтобы линия шла вместе с тем, что слышно.
 */
type Ctx = AudioContext & { outputLatency?: number };

export class ExactPlayer {
  private ctx: Ctx | null = null;
  private buf: AudioBuffer | null = null;
  private src: AudioBufferSourceNode | null = null;
  private t0 = 0;
  private from = 0;
  private to: number | null = null;
  private pos = 0;
  private endTimer = 0;
  /** Скорость на момент старта куска (lib/playbackRate.ts): позиция считается с её учётом. */
  private rate = 1;
  /** Звук кончился сам (дошёл до конца куска или записи) или поставлен на паузу. */
  onStop?: (pos: number) => void;

  /** Каналы как есть: стерео играется стерео — в наушниках слышно, кто откуда говорит (Алексей 05.11 #4228). */
  private readonly chans: Float32Array[];

  constructor(x: Float32Array | Float32Array[], private readonly sr: number) {
    this.chans = Array.isArray(x) ? x : [x];
  }

  get duration(): number {
    return this.chans[0].length / this.sr;
  }

  get playing(): boolean {
    return this.src != null;
  }

  private ensure(): Ctx {
    if (!this.ctx) this.ctx = new AudioContext() as Ctx;
    if (!this.buf) {
      this.buf = this.ctx.createBuffer(this.chans.length, this.chans[0].length, this.sr);
      this.chans.forEach((c, i) => this.buf!.copyToChannel(c, i));
    }
    return this.ctx;
  }

  private latency(): number {
    const c = this.ctx;
    return c ? (Number(c.outputLatency) || 0) + (Number(c.baseLatency) || 0) : 0;
  }

  /** Где сейчас звук, секунды записи. */
  position(): number {
    if (!this.src || !this.ctx) return this.pos;
    const p = this.from + Math.max(0, this.ctx.currentTime - this.t0 - this.latency()) * this.rate;
    return Math.min(p, this.to ?? this.duration);
  }

  /** Играть с `from`; с `to` — ровно до него и остановиться. */
  play(from: number, to: number | null = null): void {
    const ctx = this.ensure();
    this.halt();
    window.clearTimeout(this.endTimer);
    void ctx.resume();
    const a = Math.max(0, Math.min(from, this.duration));
    const b = to == null ? null : Math.max(a, Math.min(to, this.duration));
    const src = ctx.createBufferSource();
    src.buffer = this.buf;
    // Общая скорость сайта (1× / 1.5× / 2×): кусок тот же, звучит быстрее.
    this.rate = getRate();
    src.playbackRate.value = this.rate;
    src.connect(ctx.destination);
    this.t0 = ctx.currentTime;
    this.from = a;
    this.to = b;
    if (b == null) src.start(this.t0, a);
    else src.start(this.t0, a, b - a);
    src.onended = () => {
      if (this.src !== src) return;
      this.src = null;
      this.pos = b ?? this.duration;
      // «Кончилось» приходит, когда последний отсчёт отдан устройству; слышно его
      // позже на задержку вывода — бегунок и кнопка ждут столько же.
      const end = this.pos;
      this.endTimer = window.setTimeout(() => this.onStop?.(end), this.latency() * 1000);
    };
    this.src = src;
  }

  /** Смена скорости на ходу: продолжить тот же кусок с текущего места на новой скорости. */
  applyRate(): void {
    if (!this.src) return;
    const at = this.position();
    this.play(at, this.to);
  }

  pause(): void {
    if (!this.src) return;
    this.pos = this.position();
    this.halt();
    this.onStop?.(this.pos);
  }

  /** Перемотка: если играло — продолжает с нового места до конца записи. */
  seek(t: number): void {
    const wasPlaying = this.playing;
    this.pos = Math.max(0, Math.min(t, this.duration));
    if (wasPlaying) this.play(this.pos, null);
  }

  close(): void {
    this.halt();
    window.clearTimeout(this.endTimer);
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
  }

  private halt(): void {
    const s = this.src;
    this.src = null;
    if (s) {
      s.onended = null;
      try { s.stop(); } catch { /* уже остановлен */ }
      s.disconnect();
    }
  }
}

/**
 * Отсчёты для проигрывания и полосы — смесь всех каналов. На точке с двумя
 * микрофонами запись стерео (сотрудник и гость на разных дорожках); взять только
 * getChannelData(0) значило бы слышать и рисовать одного из них. Моно отдаётся как есть.
 */
export function mixDown(buf: AudioBuffer): Float32Array {
  if (buf.numberOfChannels === 1) return buf.getChannelData(0);
  const x = new Float32Array(buf.length);
  for (let ch = 0; ch < buf.numberOfChannels; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < x.length; i++) x[i] += d[i] / buf.numberOfChannels;
  }
  return x;
}

/**
 * Хук для плеера разговоров: запись качается и декодируется один раз, когда `enabled`
 * (режим «играть один сегмент» у раскрытого разговора), и дальше отрезки играются
 * точно. Пока не готово — ready=false, и вызывающий играет по-старому.
 */
export function useExactRecording(key: string | null, load: () => Promise<Blob>, enabled: boolean) {
  const [player, setPlayer] = useState<ExactPlayer | null>(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    setPlayer(null);
  }, [key]);
  useEffect(() => {
    if (!enabled || !key || player) return;
    let alive = true;
    loadRef.current()
      .then((b) => b.arrayBuffer())
      // 24 кГц, все каналы: раньше здесь было 16 кГц и смесь в моно — срезались верха выше 8 кГц
      // и пропадало стерео, по которому слышно, кто где стоит (Алексей 05.11 #4228).
      .then((ab) => new OfflineAudioContext(2, 1, 24000).decodeAudioData(ab))
      .then((buf) => {
        if (!alive) return;
        const chans = Array.from({ length: buf.numberOfChannels }, (_, i) => buf.getChannelData(i));
        setPlayer(new ExactPlayer(chans, buf.sampleRate));
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [enabled, key, player]);
  useEffect(() => {
    if (!player) return;
    player.onStop = (t) => { setPlaying(false); setPos(t); };
    const off = subscribeRate(() => player.applyRate());
    return () => { off(); player.onStop = undefined; player.close(); };
  }, [player]);
  useEffect(() => {
    if (!playing || !player) return;
    let raf = 0;
    const tick = () => { setPos(player.position()); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, player]);
  const play = useCallback((a: number, b: number | null) => {
    if (!player) return;
    player.play(a, b); setPos(a); setPlaying(true);
  }, [player]);
  const stop = useCallback(() => { player?.pause(); }, [player]);
  return { ready: player != null, playing, pos, play, stop };
}
